import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contentItemsAPI, contentPOsAPI, taxonomyAPI, workbackTemplateAPI, contentProjectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { plannerScopesRequest } from '../authConfig';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft, CheckCircle2, Circle, MinusCircle, DollarSign, Receipt,
  ArrowUpRight, Pencil, GitBranch, CalendarClock, UserPlus, ExternalLink, ListTodo,
  Trash2, RotateCcw, Wand2, Link2, LinkIcon, RefreshCw, Info, AlertCircle, Clock3, FolderKanban, Check, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { CATALOG_STATUS_STYLES, TRACK_OPTIONS, BUILD_TYPE_OPTIONS, MS_PRIORITY_OPTIONS, MS_PRIORITY_STYLES, rankWorkbackTemplates } from './ContentItemsPage';
import { CatalogLinkDialog } from '../components/content-items/CatalogLinkDialog';
import { LinkExistingDeliveryDialog } from '../components/content-items/LinkExistingDeliveryDialog';
import { EntityCustomFields } from '../components/shared/EntityCustomFields';

const ACTIVITY_STATUS_META = {
  pending: { icon: Circle, color: 'text-muted-foreground', label: 'Pending' },
  in_progress: { icon: Clock3, color: 'text-blue-600', label: 'In Progress' },
  completed: { icon: CheckCircle2, color: 'text-green-600', label: 'Completed' },
  not_applicable: { icon: MinusCircle, color: 'text-gray-400', label: 'N/A' },
};

const PROJECTION_STYLES = {
  delivered: { label: 'Delivered', className: 'text-green-600' },
  retired: { label: 'Retired', className: 'text-gray-500' },
  overdue: { label: 'Overdue', className: 'text-red-600' },
  at_risk: { label: 'At Risk', className: 'text-amber-600' },
  on_track: { label: 'On Track', className: 'text-blue-600' },
  no_target_set: { label: 'No Target Set', className: 'text-muted-foreground' },
};

const TRIAGE_PRIORITY_STYLES = {
  high: { label: 'High', className: 'bg-red-100 text-red-700 border-red-300' },
  medium: { label: 'Medium', className: 'bg-amber-100 text-amber-700 border-amber-300' },
  low: { label: 'Low', className: 'bg-slate-100 text-slate-600 border-slate-300' },
};

const phaseDateClass = (targetDate) => {
  if (!targetDate) return 'text-muted-foreground';
  const days = Math.ceil((new Date(targetDate) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'text-red-600 font-medium';
  if (days <= 7) return 'text-red-600';
  if (days <= 14) return 'text-amber-600';
  return 'text-muted-foreground';
};

export const ContentItemDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getGraphToken, user } = useAuth();
  const [item, setItem] = useState(null);
  const [inlineField, setInlineField] = useState(null);
  const [inlineValue, setInlineValue] = useState('');
  const [inlineSaving, setInlineSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState([]);
  const [workbackTemplates, setWorkbackTemplates] = useState([]);
  const [calculatedGoLive, setCalculatedGoLive] = useState('');
  const [editForm, setEditForm] = useState({
    title: '', ms_priority: '', content_po_id: '', po_amount: '', po_receipt_date: '',
    overall_status: 'active', target_go_live_date: '', workback_template_id: '', go_live_override_reason: '',
    ms_repo_url: '', sharepoint_ppt_link: '', custom_fields: {}, project_id: '',
  });
  const [assignTarget, setAssignTarget] = useState(null); // { phase_number, activity_name }
  const [assignEmail, setAssignEmail] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [phaseMetaTarget, setPhaseMetaTarget] = useState(null); // phase object being edited
  const [phaseMetaForm, setPhaseMetaForm] = useState({ target_date: '', owner_name: '' });
  const [phaseMetaSaving, setPhaseMetaSaving] = useState(false);
  const [applyingWorkback, setApplyingWorkback] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [linkExistingOpen, setLinkExistingOpen] = useState(false);
  const [catalogActionLoading, setCatalogActionLoading] = useState(false);
  const [readinessOptions, setReadinessOptions] = useState(TRACK_OPTIONS);
  const [taxonomyCategories, setTaxonomyCategories] = useState([]);
  const [customFieldKeys, setCustomFieldKeys] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activityStatusOptions, setActivityStatusOptions] = useState([]);
  const [triageDialogOpen, setTriageDialogOpen] = useState(false);
  const [triageNote, setTriageNote] = useState('');
  const [triagePriority, setTriagePriority] = useState('medium');
  const [triageLogOpen, setTriageLogOpen] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null); // triage entry being resolved
  const [resolutionNote, setResolutionNote] = useState('');
  const [editTriageTarget, setEditTriageTarget] = useState(null); // triage entry being edited (stays open)
  const [editTriageNote, setEditTriageNote] = useState('');
  const [editTriagePriority, setEditTriagePriority] = useState('medium');
  const [proposedForm, setProposedForm] = useState({ proposed_date: '', note: '' });
  const [savingTriage, setSavingTriage] = useState(false);

  useEffect(() => {
    taxonomyAPI.getAll().then(setTaxonomyCategories).catch(() => {});
  }, []);

  useEffect(() => {
    taxonomyAPI.getCategory('readiness_stage').then(cat => {
      const active = cat.items.filter(i => i.active);
      if (active.length) setReadinessOptions(active);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    taxonomyAPI.getCategory('content_activity_status').then(cat => {
      const active = (cat.items || []).filter(i => i.active);
      if (active.length) setActivityStatusOptions(active);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await contentItemsAPI.getById(id);
      setItem(data);
      setEditForm({
        title: data.title || '',
        ms_priority: data.ms_priority || '',
        content_po_id: data.content_po_id || '',
        po_amount: data.po_amount ?? '',
        po_receipt_date: data.po_receipt_date || '',
        overall_status: data.overall_status || 'active',
        target_go_live_date: data.target_go_live_date || '',
        workback_template_id: data.workback_template_id || '',
        go_live_override_reason: data.go_live_override_reason || '',
        ms_repo_url: data.ms_repo_url || '',
        sharepoint_ppt_link: data.sharepoint_ppt_link || '',
        custom_fields: data.custom_fields || {},
        project_id: data.project_id || '',
      });
      setCustomFieldKeys(Object.keys(data.custom_fields || {}));
      setCalculatedGoLive(data.calculated_go_live_date || '');
    } catch { toast.error('Failed to load content item'); }
    finally { setLoading(false); }
  }, [id]);

  const saveInline = async () => {
    if (!inlineField) return;
    setInlineSaving(true);
    try {
      await contentItemsAPI.update(item.id, { [inlineField]: inlineValue.trim() || null });
      toast.success('Link updated');
      setInlineField(null);
      await load();
    } catch { toast.error('Failed to update link'); }
    finally { setInlineSaving(false); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    contentPOsAPI.getActive().then(setPos).catch(() => {});
    workbackTemplateAPI.list().then(setWorkbackTemplates).catch(() => {});
    contentProjectsAPI.getAll().then(setProjects).catch(() => {});
  }, []);

  const loadDeliveries = useCallback(() => {
    contentItemsAPI.getDeliveries(id).then(d => setDeliveries(d.deliveries || [])).catch(() => {});
  }, [id]);

  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);

  const handleRaiseTriage = async () => {
    if (!triageNote.trim()) { toast.error('Add a short note on what needs a decision'); return; }
    setSavingTriage(true);
    try {
      const updated = await contentItemsAPI.raiseTriage(id, { note: triageNote.trim(), raised_by: user?.name || user?.email, priority: triagePriority });
      setItem(updated);
      setTriageDialogOpen(false);
      setTriageNote('');
      setTriagePriority('medium');
      toast.success('Flagged for triage at the next SCRUM call');
    } catch { toast.error('Failed to raise triage'); }
    finally { setSavingTriage(false); }
  };

  const handleResolveTriage = async () => {
    setSavingTriage(true);
    try {
      const updated = await contentItemsAPI.resolveTriage(id, resolveTarget.id, { resolution_note: resolutionNote.trim() || null });
      setItem(updated);
      setResolveTarget(null);
      setResolutionNote('');
      toast.success('Triage item resolved');
    } catch { toast.error('Failed to resolve triage item'); }
    finally { setSavingTriage(false); }
  };

  const openEditTriage = (entry) => {
    setEditTriageTarget(entry);
    setEditTriageNote(entry.note);
    setEditTriagePriority(entry.priority || 'medium');
  };

  const handleEditTriageSave = async () => {
    if (!editTriageNote.trim()) { toast.error('Note cannot be empty'); return; }
    setSavingTriage(true);
    try {
      const updated = await contentItemsAPI.editTriage(id, editTriageTarget.id, { note: editTriageNote.trim(), priority: editTriagePriority });
      setItem(updated);
      setEditTriageTarget(null);
      toast.success('Triage note updated');
    } catch { toast.error('Failed to update triage note'); }
    finally { setSavingTriage(false); }
  };

  const handleAddProposedDelivery = async () => {
    if (!proposedForm.proposed_date) { toast.error('Pick a proposed delivery date'); return; }
    try {
      const updated = await contentItemsAPI.addProposedDelivery(id, proposedForm);
      setItem(updated);
      setProposedForm({ proposed_date: '', note: '' });
      loadDeliveries();
      toast.success('Proposed delivery date added');
    } catch { toast.error('Failed to add proposed delivery'); }
  };

  const handleRemoveProposedDelivery = async (entryId) => {
    try {
      await contentItemsAPI.removeProposedDelivery(id, entryId);
      loadDeliveries();
      load();
    } catch { toast.error('Failed to remove proposed delivery'); }
  };

  const handleUnlinkDelivery = async (d) => {
    try {
      await contentItemsAPI.unlinkDelivery(id, d.source, d.source_id);
      toast.success('Delivery unlinked');
      loadDeliveries();
    } catch { toast.error('Failed to unlink delivery'); }
  };

  const handleActivityChange = async (phaseNumber, activityName, status) => {
    try {
      const updated = await contentItemsAPI.updateActivity(id, { phase_number: phaseNumber, activity_name: activityName, status });
      setItem(updated);
      toast.success('Activity updated');
    } catch { toast.error('Failed to update activity'); }
  };

  const handleAssignSubmit = async () => {
    if (!assignEmail.trim()) { toast.error("Enter the content developer's email"); return; }
    setAssignLoading(true);
    try {
      const graphToken = await getGraphToken(plannerScopesRequest.scopes);
      const result = await contentItemsAPI.assignPlannerTask(id, {
        phase_number: assignTarget.phase_number,
        activity_name: assignTarget.activity_name,
        developer_email: assignEmail.trim(),
      }, graphToken);
      toast.success(result.message);
      setAssignTarget(null);
      setAssignEmail('');
      load();
    } catch (e) {
      if (e.message === 'NO_MSAL_ACCOUNT') {
        toast.error('Please sign in with Microsoft SSO to assign Planner tasks');
      } else {
        toast.error(e.response?.data?.detail || e.message || 'Failed to assign Planner task');
      }
    } finally { setAssignLoading(false); }
  };

  const handleEditSave = async () => {
    if (!editForm.title.trim()) { toast.error('Title cannot be empty'); return; }
    if (calculatedGoLive && editForm.target_go_live_date !== calculatedGoLive && !editForm.go_live_override_reason?.trim()) {
      toast.error('Please add a reason for changing the auto-calculated go-live date'); return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        ms_priority: editForm.ms_priority || null,
        content_po_id: editForm.content_po_id || null,
        po_amount: editForm.po_amount !== '' ? parseFloat(editForm.po_amount) : null,
        po_receipt_date: editForm.po_receipt_date || null,
        overall_status: editForm.overall_status,
        target_go_live_date: editForm.target_go_live_date || null,
        workback_template_id: editForm.workback_template_id || null,
        go_live_override_reason: editForm.go_live_override_reason || null,
        calculated_go_live_date: calculatedGoLive || null,
        ms_repo_url: editForm.ms_repo_url?.trim() || null,
        sharepoint_ppt_link: editForm.sharepoint_ppt_link?.trim() || null,
        custom_fields: editForm.custom_fields || {},
        project_id: editForm.project_id || null,
      };
      const updated = await contentItemsAPI.update(id, payload);
      setItem(updated);
      setEditOpen(false);
      toast.success('Content item updated');
    } catch { toast.error('Failed to update content item'); }
    finally { setSaving(false); }
  };

  const handleRecalculateGoLive = async () => {
    if (!editForm.workback_template_id || !editForm.po_receipt_date) {
      toast.error('Select a Workback Template and PO Receipt Date first'); return;
    }
    try {
      const res = await workbackTemplateAPI.calculateGoLive(editForm.workback_template_id, editForm.po_receipt_date);
      setCalculatedGoLive(res.go_live_date);
      setEditForm(f => ({ ...f, target_go_live_date: res.go_live_date, go_live_override_reason: '' }));
      toast.success(`Calculated go-live: ${res.go_live_date}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to calculate go-live date');
    }
  };

  const handleApplyWorkback = async () => {
    setApplyingWorkback(true);
    try {
      const updated = await contentItemsAPI.applyWorkback(id);
      setItem(updated);
      toast.success('Workback plan applied — target dates recalculated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply workback template');
    } finally { setApplyingWorkback(false); }
  };

  const openPhaseMeta = (phase) => {
    setPhaseMetaTarget(phase);
    setPhaseMetaForm({ target_date: phase.target_date || '', owner_name: phase.owner_name || '' });
  };

  const handlePhaseMetaSave = async () => {
    setPhaseMetaSaving(true);
    try {
      const updated = await contentItemsAPI.updatePhaseMeta(id, {
        phase_number: phaseMetaTarget.phase_number,
        target_date: phaseMetaForm.target_date || null,
        owner_name: phaseMetaForm.owner_name,
      });
      setItem(updated);
      setPhaseMetaTarget(null);
      toast.success('Phase details updated');
    } catch { toast.error('Failed to update phase details'); }
    finally { setPhaseMetaSaving(false); }
  };

  const handleResetToTemplate = async () => {
    setPhaseMetaSaving(true);
    try {
      const updated = await contentItemsAPI.updatePhaseMeta(id, {
        phase_number: phaseMetaTarget.phase_number, reset_manual: true,
      });
      setItem(updated);
      setPhaseMetaTarget(null);
      toast.success('Reset to Workback Template');
    } catch { toast.error('Failed to reset phase'); }
    finally { setPhaseMetaSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await contentItemsAPI.delete(id);
      toast.success('Content item deleted');
      navigate('/content-items');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete content item');
      setDeleteOpen(false);
    } finally { setDeleting(false); }
  };

  const handleCatalogSelect = async (course) => {
    setCatalogDialogOpen(false);
    if (!course) return;
    setCatalogActionLoading(true);
    try {
      const updated = await contentItemsAPI.linkCatalog(id, course.course_id);
      setItem(updated);
      toast.success(`Linked to catalog course "${course.title}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link catalog course');
    } finally { setCatalogActionLoading(false); }
  };

  const handleRefreshCatalog = async () => {
    setCatalogActionLoading(true);
    try {
      const updated = await contentItemsAPI.refreshCatalog(id);
      setItem(updated);
      toast.success('Catalog metadata refreshed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to refresh from catalog');
    } finally { setCatalogActionLoading(false); }
  };

  const handleUnlinkCatalog = async () => {
    setCatalogActionLoading(true);
    try {
      const updated = await contentItemsAPI.unlinkCatalog(id);
      setItem(updated);
      toast.success('Unlinked from Catalog course');
    } catch {
      toast.error('Failed to unlink');
    } finally { setCatalogActionLoading(false); }
  };

  if (loading || !item) {
    return <div className="space-y-4"><div className="h-24 bg-muted animate-pulse rounded-lg" /><div className="h-64 bg-muted animate-pulse rounded-lg" /></div>;
  }

  const sortedPhases = [...item.phases].sort((a, b) => a.phase_number - b.phase_number);
  const goLivePhase = sortedPhases.find(p => p.is_go_live) || sortedPhases[sortedPhases.length - 1];
  const sequentialPhases = sortedPhases.filter(p => p.phase_number <= goLivePhase.phase_number);
  const parallelPhases = sortedPhases.filter(p => p.phase_number > goLivePhase.phase_number);
  const parallelUnlocked = item.phase_progress?.[goLivePhase.phase_number]?.status === 'completed';

  const renderPhaseCard = (phase) => {
    const progress = item.phase_progress?.[phase.phase_number] || { completed_count: 0, total_count: phase.activities.length, status: 'not_started' };
    const pct = progress.total_count > 0 ? Math.round((progress.completed_count / progress.total_count) * 100) : 0;
    return (
      <Card key={phase.phase_number} data-testid={`phase-card-${phase.phase_number}`} className={progress.status === 'completed' ? 'border-green-300' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-lime/20 text-lime-foreground text-xs flex items-center justify-center font-bold">{phase.phase_number}</span>
              {phase.phase_name}
              {phase.is_go_live && <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid={`go-live-phase-badge-${phase.phase_number}`}>Go-Live</Badge>}
            </CardTitle>
            <span className="text-xs text-muted-foreground">{progress.completed_count}/{progress.total_count}</span>
          </div>
          <Progress value={pct} className="h-1.5 mt-1" />
          <button
            onClick={() => openPhaseMeta(phase)}
            className="flex items-center gap-2 text-xs mt-1.5 hover:underline"
            data-testid={`phase-meta-edit-btn-${phase.phase_number}`}
          >
            <span className={phaseDateClass(phase.target_date)}>
              <CalendarClock className="w-3 h-3 inline mr-1" />
              {phase.target_date ? `Due ${phase.target_date}${phase.target_date_manual ? ' (manual)' : ''}` : 'Set target date'}
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{phase.owner_name ? `Owner: ${phase.owner_name}` : 'Set owner'}</span>
            <Pencil className="w-3 h-3 text-muted-foreground" />
          </button>
        </CardHeader>
        <CardContent className="space-y-2.5 pt-0">
          {phase.activities.map((activity, idx) => {
            const meta = ACTIVITY_STATUS_META[activity.status] || ACTIVITY_STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div key={idx} className="flex items-start justify-between gap-2 text-sm py-1" data-testid={`activity-${phase.phase_number}-${idx}`}>
                <div className="flex items-start gap-2 flex-1">
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.color}`} />
                  <div>
                    <p className={activity.status === 'not_applicable' ? 'text-muted-foreground line-through' : ''}>{activity.name}</p>
                    {activity.completed_date && <p className="text-xs text-muted-foreground">Completed {activity.completed_date}</p>}
                    {activity.assigned_to_name ? (
                      <a href={activity.planner_web_url} target="_blank" rel="noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5"
                        data-testid={`activity-assigned-${phase.phase_number}-${idx}`}>
                        <ListTodo className="w-3 h-3" />Assigned to {activity.assigned_to_name}<ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <button onClick={() => { setAssignTarget({ phase_number: phase.phase_number, activity_name: activity.name }); setAssignEmail(''); }}
                        className="text-xs text-lime-600 hover:underline flex items-center gap-1 mt-0.5"
                        data-testid={`activity-assign-btn-${phase.phase_number}-${idx}`}>
                        <UserPlus className="w-3 h-3" />Assign to Planner
                      </button>
                    )}
                  </div>
                </div>
                <Select value={activity.status} onValueChange={(v) => handleActivityChange(phase.phase_number, activity.name, v)}>
                  <SelectTrigger className="h-7 w-[110px] text-xs flex-shrink-0" data-testid={`activity-select-${phase.phase_number}-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(activityStatusOptions.length > 0 ? activityStatusOptions : [
                      { value: 'pending', label: 'Pending' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'not_applicable', label: 'N/A' },
                    ]).map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="content-item-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/content-items')} data-testid="back-to-content-items-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />Back to Content Items
      </Button>

      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-heading font-bold">{item.title}</h1>
                <Badge className={CATALOG_STATUS_STYLES[item.catalog_status]?.bg}>{CATALOG_STATUS_STYLES[item.catalog_status]?.label}</Badge>
                {item.ms_priority && <Badge className={MS_PRIORITY_STYLES[item.ms_priority]} data-testid="content-item-priority-badge">{item.ms_priority}</Badge>}
              </div>
              <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground">
                <Badge variant="outline">{item.technology_area}</Badge>
                {item.solution_area && <Badge variant="outline" className="bg-slate-50">{item.solution_area}</Badge>}
                {item.asset_type && <Badge variant="outline" className="bg-slate-50" data-testid="asset-type-badge">{item.asset_type}</Badge>}
                {Object.entries(item.custom_fields || {}).map(([key, value]) => {
                  const cat = taxonomyCategories.find(c => c.id === key);
                  const vals = Array.isArray(value) ? value : [value];
                  const labels = vals.map(v => cat?.items.find(i => i.value === v)?.label || v).filter(Boolean).join(', ');
                  if (!labels) return null;
                  return <Badge key={key} variant="outline" className="bg-slate-50" data-testid={`custom-field-badge-${key}`}>{cat?.label || key}: {labels}</Badge>;
                })}
                <span>•</span>
                <span>{readinessOptions.find(t => t.value === item.content_track)?.label || item.content_track}</span>
                <span>•</span>
                <span>{BUILD_TYPE_OPTIONS.find(b => b.value === item.build_type)?.label}</span>
              </div>
              {item.parent_item && (
                <button onClick={() => navigate(`/content-items/${item.parent_item.id}`)} className="flex items-center gap-1.5 text-sm text-lime-600 hover:underline" data-testid="parent-course-link">
                  <GitBranch className="w-3.5 h-3.5" />Parent course: {item.parent_item.title}<ArrowUpRight className="w-3 h-3" />
                </button>
              )}
              {item.project_name && (
                <button onClick={() => navigate(`/content-projects/${item.project_id}`)} className="flex items-center gap-1.5 text-sm text-lime-600 hover:underline" data-testid="content-item-project-link">
                  <FolderKanban className="w-3.5 h-3.5" />Project: {item.project_name}<ArrowUpRight className="w-3 h-3" />
                </button>
              )}
              <p className="text-sm font-medium text-muted-foreground">Current Phase: <span className="text-foreground">{item.current_phase?.label}</span></p>
              {item.po_receipt_date && (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-muted-foreground">PO Receipt Date: {item.po_receipt_date}</p>
                  <button onClick={handleApplyWorkback} disabled={applyingWorkback}
                    className="text-xs text-lime-600 hover:underline flex items-center gap-1" data-testid="apply-workback-btn">
                    <Wand2 className="w-3 h-3" />{applyingWorkback ? 'Applying...' : 'Apply Workback Plan'}
                  </button>
                </div>
              )}
              {(item.ms_repo_url || item.sharepoint_ppt_link) && (
                <div className="flex items-center gap-4 flex-wrap">
                  {item.ms_repo_url && (
                    <a href={item.ms_repo_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs text-lime-600 hover:underline" data-testid="ms-repo-url-link">
                      <LinkIcon className="w-3 h-3" />MS Repo Content
                    </a>
                  )}
                  {item.sharepoint_ppt_link && (
                    <a href={item.sharepoint_ppt_link} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs text-lime-600 hover:underline" data-testid="sharepoint-ppt-link">
                      <LinkIcon className="w-3 h-3" />SharePoint PPT Content
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col items-start md:items-end gap-2 flex-shrink-0">
              <div className="flex items-center gap-2">
                {!item.needs_triage && (
                  <Button variant="outline" size="sm" onClick={() => setTriageDialogOpen(true)} className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="raise-triage-btn">
                    <AlertCircle className="w-3.5 h-3.5 mr-2" />Flag for Triage
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-content-item-btn"><Pencil className="w-3.5 h-3.5 mr-2" />Edit</Button>
                <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-content-item-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-2" />Delete
                </Button>
              </div>
              {item.content_po_number && (
                <div className="flex items-center gap-1.5 text-sm"><Receipt className="w-3.5 h-3.5 text-muted-foreground" />{item.content_po_number}
                  {item.po_amount && <span className="flex items-center text-muted-foreground"><DollarSign className="w-3 h-3" />{item.po_amount.toLocaleString()}</span>}
                </div>
              )}
              <div className="flex items-center gap-1.5 text-sm" data-testid="content-item-go-live-projection">
                <CalendarClock className="w-3.5 h-3.5 text-muted-foreground" />
                {item.actual_go_live_date ? (
                  <span className="text-green-600">Live since {item.actual_go_live_date}</span>
                ) : item.target_go_live_date ? (
                  <span className={PROJECTION_STYLES[item.availability_projection]?.className}>
                    Target {item.target_go_live_date} · {PROJECTION_STYLES[item.availability_projection]?.label}
                  </span>
                ) : (
                  <span className="text-muted-foreground">No go-live target set</span>
                )}
                {item.go_live_slipped_days > 0 && (
                  <Badge className="bg-red-100 text-red-700 text-[10px] flex items-center gap-1" data-testid="go-live-slipped-badge">
                    <AlertCircle className="w-3 h-3" />Slipped {item.go_live_slipped_days}d vs. template
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="item-details-card">
        <CardContent className="pt-5">
          <p className="text-sm font-semibold mb-3">Item Details</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Overall Status</p>
              <p data-testid="detail-overall-status">{{ active: 'Active', on_hold: 'On Hold', retired: 'Retired' }[item.overall_status] || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Project</p>
              {item.project_name ? (
                <button onClick={() => navigate(`/content-projects/${item.project_id}`)} className="flex items-center gap-1 text-lime-700 hover:underline" data-testid="detail-project">
                  <FolderKanban className="w-3.5 h-3.5" />{item.project_name}
                </button>
              ) : <p className="text-muted-foreground" data-testid="detail-project">Standalone (no project)</p>}
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Workback Template</p>
              <p data-testid="detail-workback-template">{workbackTemplates.find(t => t.id === item.workback_template_id)?.title || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">PO Receipt Date</p>
              <p data-testid="detail-po-receipt-date">{item.po_receipt_date || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">MS Repo Course URL</p>
              {inlineField === 'ms_repo_url' ? (
                <div className="flex items-center gap-1">
                  <Input autoFocus value={inlineValue} onChange={e => setInlineValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveInline(); if (e.key === 'Escape') setInlineField(null); }} placeholder="https://..." className="h-8 text-sm" data-testid="inline-ms-repo-url-input" />
                  <Button size="icon" className="h-8 w-8 bg-lime text-lime-foreground hover:bg-lime/90" disabled={inlineSaving} onClick={saveInline} data-testid="inline-ms-repo-url-save"><Check className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setInlineField(null)} data-testid="inline-ms-repo-url-cancel"><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  {item.ms_repo_url ? (
                    <a href={item.ms_repo_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-lime-600 hover:underline break-all" data-testid="detail-ms-repo-url"><LinkIcon className="w-3 h-3 flex-shrink-0" />Open link</a>
                  ) : <span className="text-muted-foreground" data-testid="detail-ms-repo-url">—</span>}
                  <button onClick={() => { setInlineField('ms_repo_url'); setInlineValue(item.ms_repo_url || ''); }} className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground" data-testid="inline-edit-ms-repo-url-btn"><Pencil className="w-3 h-3" /></button>
                </div>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">SharePoint (ContentWeb) PPT</p>
              {inlineField === 'sharepoint_ppt_link' ? (
                <div className="flex items-center gap-1">
                  <Input autoFocus value={inlineValue} onChange={e => setInlineValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveInline(); if (e.key === 'Escape') setInlineField(null); }} placeholder="https://..." className="h-8 text-sm" data-testid="inline-sharepoint-ppt-link-input" />
                  <Button size="icon" className="h-8 w-8 bg-lime text-lime-foreground hover:bg-lime/90" disabled={inlineSaving} onClick={saveInline} data-testid="inline-sharepoint-ppt-link-save"><Check className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setInlineField(null)} data-testid="inline-sharepoint-ppt-link-cancel"><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  {item.sharepoint_ppt_link ? (
                    <a href={item.sharepoint_ppt_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-lime-600 hover:underline break-all" data-testid="detail-sharepoint-ppt-link"><LinkIcon className="w-3 h-3 flex-shrink-0" />Open link</a>
                  ) : <span className="text-muted-foreground" data-testid="detail-sharepoint-ppt-link">—</span>}
                  <button onClick={() => { setInlineField('sharepoint_ppt_link'); setInlineValue(item.sharepoint_ppt_link || ''); }} className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground" data-testid="inline-edit-sharepoint-ppt-link-btn"><Pencil className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {item.needs_triage && (
        <Card className="border-amber-400 bg-amber-50" data-testid="needs-triage-banner">
          <CardContent className="pt-5">
            <p className="flex items-center gap-2 text-amber-900 font-semibold mb-3">
              <AlertCircle className="w-4 h-4" />Needs Triage at Next SCRUM Call
            </p>
            <div className="space-y-2">
              {(item.triage_log || []).filter(e => e.status === 'open').map(entry => (
                <div key={entry.id} className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-lg px-3 py-2" data-testid={`open-triage-entry-${entry.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${TRIAGE_PRIORITY_STYLES[entry.priority || 'medium'].className}`} data-testid={`triage-priority-badge-${entry.id}`}>
                        {TRIAGE_PRIORITY_STYLES[entry.priority || 'medium'].label}
                      </Badge>
                      <p className="text-sm text-amber-900">{entry.note}</p>
                    </div>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Raised {entry.raised_by ? `by ${entry.raised_by} ` : ''}on {new Date(entry.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEditTriage(entry)} data-testid={`edit-triage-btn-${entry.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setResolveTarget(entry)} data-testid={`resolve-triage-btn-${entry.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Resolve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="catalog-info-card">
        <CardContent className="pt-5">
          {item.catalog_course_id ? (
            <div className="flex flex-col md:flex-row justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                {item.catalog_thumbnail_url && (
                  <img src={`https://catalog.skillupwithlevelup.com${item.catalog_thumbnail_url}`} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0 hidden sm:block" />
                )}
                <div className="min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm font-semibold">Linked to Catalog: {item.catalog_title}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(item.catalog_solution_area || []).map(sa => <Badge key={sa} variant="outline" className="text-[10px]">{sa}</Badge>)}
                    {item.catalog_course_type && <Badge variant="outline" className="text-[10px] bg-slate-50">{item.catalog_course_type}</Badge>}
                    {item.catalog_level && <span className="text-[11px] text-muted-foreground">{item.catalog_level}</span>}
                    {item.catalog_certifications?.length > 0 && <span className="text-[11px] text-muted-foreground">Certs: {item.catalog_certifications.join(', ')}</span>}
                  </div>
                  {item.catalog_ms_course_id && (
                    <p className="text-[11px] text-muted-foreground">MS Course ID: <span className="font-mono text-foreground" data-testid="ms-course-id-value">{item.catalog_ms_course_id}</span></p>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <Label className="text-[11px] flex items-center gap-1 text-muted-foreground">Catalog Stage <Info className="w-3 h-3" /></Label>
                    <Badge variant="outline" className="text-[10px] text-muted-foreground opacity-70 cursor-not-allowed" data-testid="catalog-stage-badge">Not available via Catalog API yet</Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Last synced: {item.catalog_synced_at ? new Date(item.catalog_synced_at).toLocaleString() : '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a href="https://catalog.skillupwithlevelup.com" target="_blank" rel="noreferrer" data-testid="view-in-catalog-link">
                  <Button variant="outline" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-2" />View in Catalog</Button>
                </a>
                <Button variant="outline" size="sm" onClick={handleRefreshCatalog} disabled={catalogActionLoading} data-testid="refresh-catalog-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-2" />Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={handleUnlinkCatalog} disabled={catalogActionLoading} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="unlink-catalog-btn">
                  Unlink
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">Not linked to the Partner Skilling Catalog yet.</p>
              <Button variant="outline" size="sm" onClick={() => setCatalogDialogOpen(true)} data-testid="link-catalog-btn">
                <LinkIcon className="w-3.5 h-3.5 mr-2" />Link to Catalog Course
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <CatalogLinkDialog open={catalogDialogOpen} onOpenChange={setCatalogDialogOpen} onSelect={handleCatalogSelect} showSkip={false} title="Link to Partner Skilling Catalog" description="Search for the matching course to pull its metadata (read-only)." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="deliveries-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Linked Deliveries ({deliveries.filter(d => d.source !== 'proposed').length})</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setLinkExistingOpen(true)} className="gap-1.5 h-7 text-xs" data-testid="link-existing-delivery-btn">
              <LinkIcon className="w-3 h-3" />Link an existing delivery
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {deliveries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No deliveries linked to this course yet.</p>
            ) : (
              <div className="space-y-2">
                {deliveries.map(d => {
                  const isSession = d.source === 'private_delivery_session' && d.training_project_id;
                  const link = isSession ? `/training-projects/${d.training_project_id}`
                    : d.source === 'partner_conferences' ? `/partner-conferences/${d.source_id}`
                    : d.source === 'regional_in_person' ? `/regional-in-person/${d.source_id}`
                    : d.source === 'pprw' ? `/pprw/${d.source_id}`
                    : null;
                  const navState = isSession ? { tab: 'sessions', highlightSessionId: d.source_id } : (link ? { highlight: true } : undefined);
                  return (
                    <div
                      key={d.source_id}
                      className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2 ${link ? 'cursor-pointer hover:border-lime hover:bg-lime/5 transition-colors' : ''}`}
                      onClick={link ? () => navigate(link, navState ? { state: navState } : undefined) : undefined}
                      data-testid={`delivery-row-${d.source_id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-[10px] flex-shrink-0" data-testid={`delivery-type-badge-${d.source_id}`}>
                            {{ private_delivery_session: 'Private Delivery', partner_conferences: 'Partner Conference', regional_in_person: 'Regional In-Person', pprw: 'PPRW (1:Many)', proposed: 'Proposed (tentative)' }[d.source] || d.source}
                          </Badge>
                          <p className="text-sm font-medium">{d.date || 'No date set'}</p>
                          {d.status && <Badge variant="outline" className="text-[10px] bg-slate-50 capitalize" data-testid={`delivery-status-badge-${d.source_id}`}>{d.status}</Badge>}
                        </div>
                        {d.note && <p className="text-xs text-muted-foreground mt-0.5">{d.note}</p>}
                      </div>
                      {d.source === 'proposed' ? (
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleRemoveProposedDelivery(d.source_id); }} className="text-red-600 hover:text-red-700 flex-shrink-0" data-testid={`remove-proposed-delivery-${d.source_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      ) : (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {link && <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground" data-testid={`delivery-link-icon-${d.source_id}`} />}
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleUnlinkDelivery(d); }} className="text-red-600 hover:text-red-700 h-7 px-2" title="Unlink this delivery" data-testid={`unlink-delivery-${d.source_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex items-end gap-2 pt-2 border-t">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Proposed Delivery Date</Label>
                <Input type="date" value={proposedForm.proposed_date} onChange={e => setProposedForm(f => ({ ...f, proposed_date: e.target.value }))} data-testid="proposed-delivery-date-input" />
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Note</Label>
                <Input value={proposedForm.note} onChange={e => setProposedForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Partner ask, tentative" data-testid="proposed-delivery-note-input" />
              </div>
              <Button size="sm" onClick={handleAddProposedDelivery} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="add-proposed-delivery-btn">Add</Button>
            </div>
          </CardContent>
        </Card>

        <LinkExistingDeliveryDialog
          open={linkExistingOpen}
          onOpenChange={setLinkExistingOpen}
          contentItemId={id}
          contentItemTitle={item.title}
          onLinked={loadDeliveries}
          data-testid="link-existing-delivery-dialog-wrapper"
        />

        <Card data-testid="triage-log-card">
          <CardHeader className="pb-2">
            <button onClick={() => setTriageLogOpen(v => !v)} className="w-full flex items-center justify-between text-left" data-testid="triage-log-toggle">
              <CardTitle className="text-sm">Triage Log ({(item.triage_log || []).length})</CardTitle>
            </button>
          </CardHeader>
          <CardContent className="space-y-2">
            {(item.triage_log || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No triage history yet.</p>
            ) : (
              [...(item.triage_log || [])].reverse().slice(0, triageLogOpen ? undefined : 3).map(entry => (
                <div key={entry.id} className={`border rounded-lg px-3 py-2 ${entry.status === 'open' ? 'bg-amber-50 border-amber-200' : 'bg-muted/30'}`} data-testid={`triage-log-entry-${entry.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${TRIAGE_PRIORITY_STYLES[entry.priority || 'medium'].className}`}>
                        {TRIAGE_PRIORITY_STYLES[entry.priority || 'medium'].label}
                      </Badge>
                      <p className="text-sm truncate">{entry.note}</p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${entry.status === 'open' ? 'text-amber-700 border-amber-300' : 'text-green-700 border-green-300'}`}>
                      {entry.status === 'open' ? 'Open' : 'Resolved'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Raised {entry.raised_by ? `by ${entry.raised_by} ` : ''}on {new Date(entry.created_at).toLocaleDateString()}
                    {entry.resolved_at && ` · Resolved ${new Date(entry.resolved_at).toLocaleDateString()}`}
                  </p>
                  {entry.resolution_note && <p className="text-xs text-green-700 mt-1">Decision: {entry.resolution_note}</p>}
                </div>
              ))
            )}
            {(item.triage_log || []).length > 3 && (
              <button onClick={() => setTriageLogOpen(v => !v)} className="text-xs text-lime-600 hover:underline" data-testid="triage-log-show-more">
                {triageLogOpen ? 'Show less' : `Show all ${item.triage_log.length}`}
              </button>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <h2 className="text-lg font-heading font-semibold mb-3">Sequential Phases</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sequentialPhases.map(renderPhaseCard)}
        </div>
      </div>

      {parallelPhases.length > 0 && (
      <div>
        <h2 className="text-lg font-heading font-semibold mb-1">Parallel Workstreams</h2>
        <p className="text-sm text-muted-foreground mb-3">
          {parallelUnlocked ? 'These phases run simultaneously post Go-Live.' : `Unlocks once ${goLivePhase.phase_name} (Go Live) is complete — you can still track progress early if work starts ahead of schedule.`}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {parallelPhases.map(renderPhaseCard)}
        </div>
      </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="edit-content-item-dialog">
          <DialogHeader><DialogTitle>Edit Content Item</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Course Title</Label>
              <Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Content PO (Microsoft)</Label>
                <Select value={editForm.content_po_id} onValueChange={v => setEditForm({ ...editForm, content_po_id: v })}>
                  <SelectTrigger data-testid="edit-po-select"><SelectValue placeholder="Select PO" /></SelectTrigger>
                  <SelectContent>{pos.map(p => <SelectItem key={p.id} value={p.id}>{p.display_label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>PO Amount ($)</Label>
                <Input type="number" value={editForm.po_amount} onChange={e => setEditForm({ ...editForm, po_amount: e.target.value })} data-testid="edit-po-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>MS Priority</Label>
                <Select value={editForm.ms_priority} onValueChange={v => setEditForm({ ...editForm, ms_priority: v })}>
                  <SelectTrigger data-testid="edit-priority-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{MS_PRIORITY_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>PO Receipt Date</Label>
                <Input type="date" value={editForm.po_receipt_date} onChange={e => setEditForm({ ...editForm, po_receipt_date: e.target.value })} data-testid="edit-po-receipt-date-input" />
                <p className="text-[11px] text-muted-foreground mt-1">Auto-generates the workback plan</p>
              </div>
            </div>
            <div>
              <Label>Overall Status</Label>
              <Select value={editForm.overall_status} onValueChange={v => setEditForm({ ...editForm, overall_status: v })}>
                <SelectTrigger data-testid="edit-overall-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><FolderKanban className="w-3.5 h-3.5" />Project</Label>
              <Select value={editForm.project_id || '__none__'} onValueChange={v => setEditForm({ ...editForm, project_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="edit-project-select"><SelectValue placeholder="Standalone (no project)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Standalone (no project) —</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5" />Workback Template</Label>
              <Select value={editForm.workback_template_id} onValueChange={v => setEditForm({ ...editForm, workback_template_id: v })}>
                <SelectTrigger data-testid="edit-workback-template-select"><SelectValue placeholder="Select template (optional)" /></SelectTrigger>
                <SelectContent>
                  {rankWorkbackTemplates(workbackTemplates, item.asset_type, item.content_track).map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex items-center gap-1.5">
                        {t.title}{t.is_default ? ' (Default)' : ''}
                        {t._suggested && <Badge className="bg-lime/20 text-lime-700 text-[9px]">Suggested</Badge>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Target Go-Live Date</Label>
                <button type="button" onClick={handleRecalculateGoLive} className="text-[11px] text-lime-600 hover:underline flex items-center gap-1" data-testid="recalculate-go-live-btn">
                  <RotateCcw className="w-3 h-3" />Recalculate
                </button>
              </div>
              <Input type="date" value={editForm.target_go_live_date} onChange={e => setEditForm({ ...editForm, target_go_live_date: e.target.value })} data-testid="edit-target-date-input" />
              {calculatedGoLive && editForm.target_go_live_date !== calculatedGoLive && (
                <div className="mt-2">
                  <Label className="text-xs">Reason for changing the calculated date</Label>
                  <Textarea rows={2} value={editForm.go_live_override_reason} onChange={e => setEditForm({ ...editForm, go_live_override_reason: e.target.value })} placeholder="e.g. MS review team confirmed a later slot" data-testid="edit-go-live-override-reason-input" />
                </div>
              )}
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><LinkIcon className="w-3.5 h-3.5" />MS Repo Course URL</Label>
              <Input type="url" value={editForm.ms_repo_url} onChange={e => setEditForm({ ...editForm, ms_repo_url: e.target.value })} placeholder="https://repo.microsoft.com/..." data-testid="edit-ms-repo-url-input" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><LinkIcon className="w-3.5 h-3.5" />SharePoint (ContentWeb) PPT Link</Label>
              <Input type="url" value={editForm.sharepoint_ppt_link} onChange={e => setEditForm({ ...editForm, sharepoint_ppt_link: e.target.value })} placeholder="https://technofocus.sharepoint.com/..." data-testid="edit-sharepoint-ppt-link-input" />
            </div>
            <div className="pt-2 border-t border-border">
              <p className="text-xs font-medium text-muted-foreground mb-3 mt-2">Additional Taxonomy Fields</p>
              <EntityCustomFields
                entityType="content_item"
                values={editForm.custom_fields || {}}
                onChange={(v) => setEditForm(f => ({ ...f, custom_fields: v }))}
                isSuperAdmin={user?.role === 'superadmin'}
                currentUser={user}
                testPrefix="edit-content-custom-field"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={saving} data-testid="save-content-item-edit-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">{saving ? 'Saving...' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="planner-assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListTodo className="w-4 h-4" />Assign to Planner</DialogTitle>
            <DialogDescription>Creates a task in the "ContentDev Planner-FY27" board for "{assignTarget?.activity_name}"</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Content Developer's Email</Label>
            <Input type="email" value={assignEmail} onChange={e => setAssignEmail(e.target.value)} placeholder="developer@technofocus.in" data-testid="planner-assign-email-input" />
            <p className="text-xs text-muted-foreground mt-1">Must be a Technofocus Microsoft 365 account. Requires you to be signed in via Microsoft SSO.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTarget(null)}>Cancel</Button>
            <Button onClick={handleAssignSubmit} disabled={assignLoading} data-testid="planner-assign-submit-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
              {assignLoading ? 'Assigning...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!phaseMetaTarget} onOpenChange={(o) => !o && setPhaseMetaTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="phase-meta-dialog">
          <DialogHeader>
            <DialogTitle>Phase {phaseMetaTarget?.phase_number}: {phaseMetaTarget?.phase_name}</DialogTitle>
            <DialogDescription>Set the target completion date and accountable owner for this phase</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Target Date</Label>
              <Input type="date" value={phaseMetaForm.target_date} onChange={e => setPhaseMetaForm({ ...phaseMetaForm, target_date: e.target.value })} data-testid="phase-meta-target-date-input" />
              {phaseMetaTarget?.target_date_manual && (
                <button onClick={handleResetToTemplate} disabled={phaseMetaSaving} className="text-xs text-lime-600 hover:underline flex items-center gap-1 mt-1.5" data-testid="phase-meta-reset-btn">
                  <RotateCcw className="w-3 h-3" />Reset to Workback Template
                </button>
              )}
            </div>
            <div>
              <Label>Owner</Label>
              <Input value={phaseMetaForm.owner_name} onChange={e => setPhaseMetaForm({ ...phaseMetaForm, owner_name: e.target.value })} placeholder="e.g. Yukti, Manisha" data-testid="phase-meta-owner-input" />
              <p className="text-[11px] text-muted-foreground mt-1">Whoever's team owns this phase based on the nature of the build</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhaseMetaTarget(null)}>Cancel</Button>
            <Button onClick={handlePhaseMetaSave} disabled={phaseMetaSaving} data-testid="phase-meta-save-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
              {phaseMetaSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="content-item-detail-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{item.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the content item and its full phase/activity history. This cannot be undone.
              Deletion is blocked if other Refresh/New SKU builds reference it as their parent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="content-item-detail-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="content-item-detail-delete-confirm-btn">
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={triageDialogOpen} onOpenChange={setTriageDialogOpen}>
        <DialogContent data-testid="raise-triage-dialog">
          <DialogHeader>
            <DialogTitle>Flag for Triage</DialogTitle>
            <DialogDescription>What decision or discussion does this course need at the next SCRUM call?</DialogDescription>
          </DialogHeader>
          <Textarea
            value={triageNote}
            onChange={e => setTriageNote(e.target.value)}
            placeholder="e.g. Need your call: reuse existing lab environment or build a new one?"
            rows={3}
            data-testid="triage-note-input"
          />
          <div className="space-y-1.5">
            <Label className="text-xs">Priority</Label>
            <Select value={triagePriority} onValueChange={setTriagePriority}>
              <SelectTrigger data-testid="triage-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTriageDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRaiseTriage} disabled={savingTriage} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="raise-triage-confirm-btn">
              {savingTriage ? 'Flagging...' : 'Flag for Triage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolveTarget} onOpenChange={(o) => { if (!o) setResolveTarget(null); }}>
        <DialogContent data-testid="resolve-triage-dialog">
          <DialogHeader>
            <DialogTitle>Resolve Triage Item</DialogTitle>
            <DialogDescription>{resolveTarget?.note}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={resolutionNote}
            onChange={e => setResolutionNote(e.target.value)}
            placeholder="What was decided? (optional)"
            rows={3}
            data-testid="resolution-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveTarget(null)}>Cancel</Button>
            <Button onClick={handleResolveTriage} disabled={savingTriage} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="resolve-triage-confirm-btn">
              {savingTriage ? 'Resolving...' : 'Mark Resolved'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTriageTarget} onOpenChange={(o) => { if (!o) setEditTriageTarget(null); }}>
        <DialogContent data-testid="edit-triage-dialog">
          <DialogHeader>
            <DialogTitle>Edit Triage Note</DialogTitle>
            <DialogDescription>Update the note or priority — this stays open for the next SCRUM call.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Note</Label>
              <Textarea
                value={editTriageNote}
                onChange={e => setEditTriageNote(e.target.value)}
                placeholder="What needs a decision?"
                rows={3}
                data-testid="edit-triage-note-input"
              />
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={editTriagePriority} onValueChange={setEditTriagePriority}>
                <SelectTrigger data-testid="edit-triage-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTriageTarget(null)}>Cancel</Button>
            <Button onClick={handleEditTriageSave} disabled={savingTriage} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="save-edit-triage-btn">
              {savingTriage ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentItemDetailPage;
